import { CategoryServiceErrorType } from '@/types/CategoryTypes';

/**
 * Níveis de log disponíveis
 */
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  CRITICAL = 4
}

/**
 * Interface para entrada de log
 */
export interface LogEntry {
  timestamp: number;
  level: LogLevel;
  message: string;
  context?: string;
  data?: any;
  error?: Error;
  userId?: string;
  sessionId?: string;
  requestId?: string;
}

/**
 * Interface para configuração do logger
 */
export interface LoggerConfig {
  minLevel: LogLevel;
  maxEntries: number;
  enableConsole: boolean;
  enableStorage: boolean;
  enableRemoteLogging: boolean;
  remoteEndpoint?: string;
  contextPrefix?: string;
}

/**
 * Sistema robusto de logging e tratamento de erros
 * 
 * Características:
 * - Múltiplos níveis de log (DEBUG, INFO, WARN, ERROR, CRITICAL)
 * - Armazenamento local de logs para debug
 * - Envio de logs críticos para servidor remoto
 * - Contexto detalhado para cada erro
 * - Métricas de erro para monitoramento
 * - Rate limiting para evitar spam de logs
 */
export class ErrorLogger {
  private static instance: ErrorLogger;
  private logs: LogEntry[] = [];
  private config: LoggerConfig;
  private errorMetrics = {
    totalErrors: 0,
    errorsByType: new Map<CategoryServiceErrorType, number>(),
    errorsByLevel: new Map<LogLevel, number>(),
    lastError: null as LogEntry | null
  };
  private rateLimitMap = new Map<string, number>();
  private readonly RATE_LIMIT_WINDOW = 60000; // 1 minuto
  private readonly MAX_LOGS_PER_MINUTE = 10;

  private constructor(config?: Partial<LoggerConfig>) {
    this.config = {
      minLevel: LogLevel.INFO,
      maxEntries: 1000,
      enableConsole: true,
      enableStorage: true,
      enableRemoteLogging: false,
      contextPrefix: 'CategoryService',
      ...config
    };

    // Configurar limpeza automática de logs antigos
    this.startCleanupInterval();
  }

  /**
   * Singleton pattern
   */
  public static getInstance(config?: Partial<LoggerConfig>): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger(config);
    }
    return ErrorLogger.instance;
  }

  /**
   * Log de debug (desenvolvimento)
   */
  public debug(message: string, data?: any, context?: string): void {
    this.log(LogLevel.DEBUG, message, data, context);
  }

  /**
   * Log informativo
   */
  public info(message: string, data?: any, context?: string): void {
    this.log(LogLevel.INFO, message, data, context);
  }

  /**
   * Log de aviso
   */
  public warn(message: string, data?: any, context?: string): void {
    this.log(LogLevel.WARN, message, data, context);
  }

  /**
   * Log de erro
   */
  public error(message: string, error?: Error, data?: any, context?: string): void {
    this.log(LogLevel.ERROR, message, data, context, error);
  }

  /**
   * Log crítico (enviado para servidor remoto)
   */
  public critical(message: string, error?: Error, data?: any, context?: string): void {
    this.log(LogLevel.CRITICAL, message, data, context, error);
  }

  /**
   * Log específico para erros do CategoryService
   */
  public logCategoryServiceError(
    errorType: CategoryServiceErrorType,
    message: string,
    error?: Error,
    data?: any,
    context?: string
  ): void {
    const level = this.getLogLevelForErrorType(errorType);
    const fullMessage = `[${errorType}] ${message}`;
    
    this.log(level, fullMessage, { errorType, ...data }, context, error);
    
    // Atualizar métricas
    this.updateErrorMetrics(errorType, level);
  }

  /**
   * Método principal de logging
   */
  private log(
    level: LogLevel,
    message: string,
    data?: any,
    context?: string,
    error?: Error
  ): void {
    // Verificar nível mínimo
    if (level < this.config.minLevel) {
      return;
    }

    // Rate limiting
    const rateLimitKey = `${level}_${message.substring(0, 50)}`;
    if (this.isRateLimited(rateLimitKey)) {
      return;
    }

    const logEntry: LogEntry = {
      timestamp: Date.now(),
      level,
      message,
      context: context || this.config.contextPrefix,
      data,
      error,
      userId: this.getCurrentUserId(),
      sessionId: this.getSessionId(),
      requestId: this.generateRequestId()
    };

    // Adicionar aos logs locais
    this.addToLocalLogs(logEntry);

    // Log no console se habilitado
    if (this.config.enableConsole) {
      this.logToConsole(logEntry);
    }

    // Armazenar localmente se habilitado
    if (this.config.enableStorage) {
      this.saveToLocalStorage(logEntry);
    }

    // Enviar para servidor remoto se crítico
    if (level >= LogLevel.ERROR && this.config.enableRemoteLogging) {
      this.sendToRemoteServer(logEntry);
    }

    // Atualizar métricas
    this.updateMetrics(level, logEntry);
  }

  /**
   * Determina o nível de log baseado no tipo de erro
   */
  private getLogLevelForErrorType(errorType: CategoryServiceErrorType): LogLevel {
    switch (errorType) {
      case CategoryServiceErrorType.NETWORK_ERROR:
        return LogLevel.WARN;
      case CategoryServiceErrorType.VALIDATION_ERROR:
        return LogLevel.WARN;
      case CategoryServiceErrorType.PERMISSION_ERROR:
        return LogLevel.ERROR;
      case CategoryServiceErrorType.NOT_FOUND:
        return LogLevel.INFO;
      case CategoryServiceErrorType.CACHE_ERROR:
        return LogLevel.WARN;
      case CategoryServiceErrorType.UNKNOWN_ERROR:
      default:
        return LogLevel.ERROR;
    }
  }

  /**
   * Verifica rate limiting
   */
  private isRateLimited(key: string): boolean {
    const now = Date.now();
    const lastLog = this.rateLimitMap.get(key) || 0;
    
    if (now - lastLog < this.RATE_LIMIT_WINDOW) {
      return true;
    }
    
    this.rateLimitMap.set(key, now);
    return false;
  }

  /**
   * Adiciona log à lista local
   */
  private addToLocalLogs(logEntry: LogEntry): void {
    this.logs.push(logEntry);
    
    // Limitar número de logs
    if (this.logs.length > this.config.maxEntries) {
      this.logs = this.logs.slice(-this.config.maxEntries);
    }
  }

  /**
   * Log no console com formatação apropriada
   */
  private logToConsole(logEntry: LogEntry): void {
    const timestamp = new Date(logEntry.timestamp).toISOString();
    const context = logEntry.context ? `[${logEntry.context}]` : '';
    const prefix = `${timestamp} ${context}`;

    switch (logEntry.level) {
      case LogLevel.DEBUG:
        console.debug(`${prefix} DEBUG:`, logEntry.message, logEntry.data);
        break;
      case LogLevel.INFO:
        console.info(`${prefix} INFO:`, logEntry.message, logEntry.data);
        break;
      case LogLevel.WARN:
        console.warn(`${prefix} WARN:`, logEntry.message, logEntry.data);
        break;
      case LogLevel.ERROR:
        console.error(`${prefix} ERROR:`, logEntry.message, logEntry.error, logEntry.data);
        break;
      case LogLevel.CRITICAL:
        console.error(`${prefix} CRITICAL:`, logEntry.message, logEntry.error, logEntry.data);
        break;
    }
  }

  /**
   * Salva log no localStorage
   */
  private saveToLocalStorage(logEntry: LogEntry): void {
    try {
      const storageKey = `${this.config.contextPrefix}_logs`;
      const existingLogs = JSON.parse(localStorage.getItem(storageKey) || '[]');
      existingLogs.push(logEntry);
      
      // Manter apenas os últimos 100 logs no localStorage
      const recentLogs = existingLogs.slice(-100);
      localStorage.setItem(storageKey, JSON.stringify(recentLogs));
    } catch (error) {
      console.warn('Failed to save log to localStorage:', error);
    }
  }

  /**
   * Envia log para servidor remoto
   */
  private async sendToRemoteServer(logEntry: LogEntry): Promise<void> {
    if (!this.config.remoteEndpoint) return;

    try {
      await fetch(this.config.remoteEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...logEntry,
          source: 'category-service',
          environment: process.env.NODE_ENV || 'development'
        })
      });
    } catch (error) {
      console.warn('Failed to send log to remote server:', error);
    }
  }

  /**
   * Atualiza métricas de erro
   */
  private updateErrorMetrics(errorType: CategoryServiceErrorType, level: LogLevel): void {
    this.errorMetrics.totalErrors++;
    
    const typeCount = this.errorMetrics.errorsByType.get(errorType) || 0;
    this.errorMetrics.errorsByType.set(errorType, typeCount + 1);
    
    const levelCount = this.errorMetrics.errorsByLevel.get(level) || 0;
    this.errorMetrics.errorsByLevel.set(level, levelCount + 1);
  }

  /**
   * Atualiza métricas gerais
   */
  private updateMetrics(level: LogLevel, logEntry: LogEntry): void {
    if (level >= LogLevel.ERROR) {
      this.errorMetrics.lastError = logEntry;
    }
  }

  /**
   * Obtém ID do usuário atual
   */
  private getCurrentUserId(): string | undefined {
    // Implementar lógica para obter ID do usuário
    // Por exemplo, do contexto de autenticação
    return undefined;
  }

  /**
   * Obtém ID da sessão
   */
  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('session_id');
    if (!sessionId) {
      sessionId = this.generateId();
      sessionStorage.setItem('session_id', sessionId);
    }
    return sessionId;
  }

  /**
   * Gera ID único para requisição
   */
  private generateRequestId(): string {
    return this.generateId();
  }

  /**
   * Gera ID único
   */
  private generateId(): string {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  /**
   * Inicia intervalo de limpeza
   */
  private startCleanupInterval(): void {
    setInterval(() => {
      this.cleanup();
    }, 5 * 60 * 1000); // A cada 5 minutos
  }

  /**
   * Limpa logs antigos e rate limits
   */
  private cleanup(): void {
    const now = Date.now();
    const oneHourAgo = now - (60 * 60 * 1000);
    
    // Limpar logs antigos
    this.logs = this.logs.filter(log => log.timestamp > oneHourAgo);
    
    // Limpar rate limits antigos
    for (const [key, timestamp] of this.rateLimitMap.entries()) {
      if (now - timestamp > this.RATE_LIMIT_WINDOW) {
        this.rateLimitMap.delete(key);
      }
    }
  }

  /**
   * Obtém logs filtrados
   */
  public getLogs(filter?: {
    level?: LogLevel;
    context?: string;
    since?: number;
    limit?: number;
  }): LogEntry[] {
    let filteredLogs = [...this.logs];

    if (filter?.level !== undefined) {
      filteredLogs = filteredLogs.filter(log => log.level >= filter.level!);
    }

    if (filter?.context) {
      filteredLogs = filteredLogs.filter(log => 
        log.context?.includes(filter.context!)
      );
    }

    if (filter?.since) {
      filteredLogs = filteredLogs.filter(log => log.timestamp >= filter.since!);
    }

    if (filter?.limit) {
      filteredLogs = filteredLogs.slice(-filter.limit);
    }

    return filteredLogs.sort((a, b) => b.timestamp - a.timestamp);
  }

  /**
   * Obtém métricas de erro
   */
  public getErrorMetrics() {
    return {
      ...this.errorMetrics,
      errorsByType: Object.fromEntries(this.errorMetrics.errorsByType),
      errorsByLevel: Object.fromEntries(this.errorMetrics.errorsByLevel)
    };
  }

  /**
   * Exporta logs para análise
   */
  public exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  /**
   * Limpa todos os logs
   */
  public clearLogs(): void {
    this.logs = [];
    this.errorMetrics = {
      totalErrors: 0,
      errorsByType: new Map(),
      errorsByLevel: new Map(),
      lastError: null
    };
  }
}

// Exportar instância singleton configurada para o CategoryService
export const categoryLogger = ErrorLogger.getInstance({
  minLevel: process.env.NODE_ENV === 'development' ? LogLevel.DEBUG : LogLevel.INFO,
  maxEntries: 500,
  enableConsole: true,
  enableStorage: true,
  enableRemoteLogging: process.env.NODE_ENV === 'production',
  contextPrefix: 'CategoryService'
});
